export type OfferId =
  | "sla-sandbox"
  | "ai-workflow-engineering"
  | "low-volume-coverage"
  | "licensed-insurance-agents";

export type OfferAccent = "amber" | "blue" | "cyan" | "emerald";

export type OfferContent = {
  intro?: string;
  paragraphs: readonly string[];
  bulletPoints?: readonly string[];
  conditions?: readonly string[];
  badge?: string;
  metrics?: readonly {
    value: string;
    label: string;
    accent?: boolean;
  }[];
  metricsNote?: string;
  comparison?: {
    label: string;
    note?: string;
    baseline: {
      title: string;
      details: readonly string[];
    };
    offer: {
      title: string;
      price: string;
      description?: string;
      details?: readonly string[];
      saving: string;
    };
  };
  useCases?: readonly {
    title: string;
    description: string;
  }[];
};

export type StrategicOffer = {
  id: OfferId;
  title: string;
  subtitle?: string;
  accent: OfferAccent;
  content: OfferContent;
  cta: {
    label: "START THE CONVERSATION" | "DISCUSS THIS OFFER";
    destination: "#contact";
  };
};

// Approved offer copy belongs in the content fields below; presentation stays in OfferModal.
export const strategicOffers: readonly StrategicOffer[] = [
  {
    id: "sla-sandbox",
    title: "90-Day SLA & Accuracy Sandbox",
    subtitle: "Prove performance before you migrate.",
    accent: "amber",
    content: {
      badge: "GUARANTEED OUTCOME",
      intro: "Validate business-critical operations in a controlled 90-day environment before committing to a broader transition.",
      paragraphs: [
        "FTO runs an end-to-end sandbox with a 99.5%+ accuracy commitment. If that threshold is not met, management fees are waived.",
        "The billable unit of work is negotiated based on your operational requirements. It may be a transaction, a call, or another measurable unit representing a completed outcome, billed at $4.50 per agreed unit.",
      ],
      metrics: [
        {
          value: "90 days",
          label: "Controlled operational validation",
          accent: true,
        },
        {
          value: "99.5%+",
          label: "Accuracy commitment",
        },
        {
          value: "$4.50 / unit",
          label: "All-in outcome cost",
        },
      ],
      metricsNote: "Minimum 2,500 transactions / month",
      comparison: {
        label: "A MORE PREDICTABLE OUTCOME MODEL",
        baseline: {
          title: "Typical Domestic / Agency Handle",
          details: ["~720s AHT baseline", "$8.00 / unit"],
        },
        offer: {
          title: "FTO Managed Outcome",
          details: [
            "Human handle baseline: $3.50 / unit",
            "Platform & AI orchestration: $1.00 / unit",
          ],
          price: "$4.50 / unit all-in",
          saving: "SAVE 43.8%",
        },
      },
      useCases: [
        {
          title: "Business-critical operations",
          description: "Validate performance before moving critical operational lines.",
        },
        {
          title: "Pre-migration confidence",
          description: "Test the delivery model before making a broader commitment.",
        },
        {
          title: "Performance accountability",
          description: "Operate against a defined 99.5%+ accuracy commitment.",
        },
      ],
    },
    cta: { label: "DISCUSS THIS OFFER", destination: "#contact" },
  },
  {
    id: "ai-workflow-engineering",
    title: "Free Custom AI Workflow Engineering",
    subtitle: "Bring custom AI into your operation—without the upfront integration cost.",
    accent: "blue",
    content: {
      badge: "ZERO-CAPEX AI",
      intro: "FTO scopes, builds, and deploys custom AI agents directly into your existing software environment—including Salesforce, Zendesk, and SAP—with upfront integration engineering absorbed by FTO.",
      paragraphs: [
        "AI pre-processing reduces human handle time from 12 minutes to under 3.5 minutes, while low-confidence and exception cases can move to human specialists for resolution.",
      ],
      metrics: [
        {
          value: "$25K–$50K",
          label: "Upfront integration cost absorbed by FTO",
          accent: true,
        },
        {
          value: "$3.75 / unit",
          label: "FTO hybrid outcome price",
        },
        {
          value: "12 min → <3.5 min",
          label: "Human handle time with AI pre-processing",
        },
      ],
      metricsNote: "Minimum: 3,000 transactions / month",
      comparison: {
        label: "A MORE EFFICIENT AI OPERATING MODEL",
        baseline: {
          title: "Legacy Manual Workflow",
          details: ["~$6.00 / unit", "Manual processing"],
        },
        offer: {
          title: "FTO Hybrid Workflow",
          details: ["AI pre-processing before human handling"],
          price: "$3.75 / unit",
          saving: "UPFRONT INTEGRATION COST ABSORBED BY FTO",
        },
      },
      useCases: [
        {
          title: "AI engineering backlog",
          description: "Move custom AI workflows into operation without taking on the upfront integration investment.",
        },
        {
          title: "High-volume manual workflows",
          description: "Use AI pre-processing to reduce repetitive handling before human intervention.",
        },
        {
          title: "Existing enterprise systems",
          description: "Deploy custom AI agents directly into environments such as Salesforce, Zendesk, and SAP.",
        },
      ],
    },
    cta: { label: "DISCUSS THIS OFFER", destination: "#contact" },
  },
  {
    id: "low-volume-coverage",
    title: "24/7/365 Low-Volume Line Coverage",
    subtitle: "Keep critical lines covered. Around the clock.",
    accent: "cyan",
    content: {
      badge: "FLAT-RATE 24/7",
      intro: "Maintain continuous live human readiness for low-volume lines without funding a dedicated 24/7 shift team.",
      paragraphs: [
        "FTO provides flat-rate pooled coverage powered by high-end offshore and premium nearshore talent for emergency dispatch, VIP escalations, and critical triage.",
      ],
      metrics: [
        {
          value: "168 hours",
          label: "Continuous live human readiness",
          accent: true,
        },
        {
          value: "$4,000 / month",
          label: "Flat-rate pooled coverage",
        },
        {
          value: "Up to 3",
          label: "Concurrent daytime calls",
        },
      ],
      comparison: {
        label: "A MORE EFFICIENT COVERAGE MODEL",
        baseline: {
          title: "Dedicated 24/7 Nearshore / Offshore Team",
          details: ["4.5 FTE benchmark", "$10,000–$12,000 / month"],
        },
        offer: {
          title: "FTO Flat-Rate Pooled Coverage",
          price: "$4,000 / month",
          description: "Maintain 24/7 readiness without carrying the cost of a dedicated nearshore or offshore shift team.",
          saving: "SAVE ~60%",
        },
      },
      useCases: [
        {
          title: "Emergency dispatch",
          description: "Live human readiness for emergency-response lines.",
        },
        {
          title: "VIP escalations",
          description: "Human coverage for priority customer escalations.",
        },
        {
          title: "Critical triage",
          description: "Live coverage for critical inquiries requiring human attention.",
        },
      ],
    },
    cta: { label: "DISCUSS THIS OFFER", destination: "#contact" },
  },
  {
    id: "licensed-insurance-agents",
    title: "State-Licensed Insurance Agents",
    subtitle: "Extend licensed capacity. Keep every interaction accountable.",
    accent: "emerald",
    content: {
      badge: "LICENSED CAPACITY",
      intro: "FTO provides credentialed, U.S. state-licensed insurance specialists across Property & Casualty and Life & Health, embedded into your existing agency, broker, or carrier environment.",
      paragraphs: [
        "Our licensed teams work within platforms such as Applied Epic, AMS360, and EZLynx, with a transparent hourly model and 100% call auditing for required state disclosures and script adherence.",
      ],
      metrics: [
        {
          value: "$32 / hour",
          label: "FTO contracted licensed rate",
          accent: true,
        },
        {
          value: "P&C + L&H",
          label: "State-licensed insurance specialists",
        },
        {
          value: "100%",
          label: "Calls audited for disclosure and script adherence",
        },
      ],
      comparison: {
        label: "A MORE EFFICIENT LICENSED CAPACITY MODEL",
        note: "The 2-agent pod is a financial comparison baseline, not a contractual minimum.",
        baseline: {
          title: "Domestic In-House Licensed Capacity",
          details: ["$48–$60 / hour", "Burdened internal rate"],
        },
        offer: {
          title: "FTO Licensed Capacity",
          price: "$32 / hour",
          description: "Credentialed licensed specialists embedded into your operation",
          saving: "38.5%\nCOST REDUCTION",
        },
      },
      useCases: [
        {
          title: "Agency operations",
          description: "Add licensed specialists directly into established agency workflows and systems.",
        },
        {
          title: "Broker & carrier environments",
          description: "Extend licensed operational capacity across regulated customer interactions.",
        },
        {
          title: "Compliance-sensitive interactions",
          description: "Support mandatory state disclosure and script-adherence requirements with 100% call auditing.",
        },
      ],
    },
    cta: { label: "DISCUSS THIS OFFER", destination: "#contact" },
  },
];
